/**
 * A pilha de overlays abertos — sheets, painéis e o menu do sanduíche —, e as duas coisas que
 * dependem dela: travar a rolagem do fundo e saber o que fechar quando alguém pede "voltar".
 *
 * <h2>Por que uma pilha só</h2>
 * A trava de rolagem já foi duas: `document.body.classList.add(...)` em quatro lugares (só um deles
 * contando) e um `document.body.style.overflow` inline no NavBar, que nem sabia do outro. O
 * resultado era fechar um painel aberto POR CIMA de um sheet e destravar a rolagem com o sheet
 * ainda na tela.
 *
 * O botão voltar do Android precisa exatamente da mesma informação — quantos overlays estão abertos
 * e qual é o do topo. Mantê-lo numa segunda pilha, registrada nos mesmos quatro pontos, seria
 * recriar de propósito a duplicação que causou aquele bug. Então é uma pilha, com dois usos.
 *
 * <h2>Por que a rolagem precisa de position: fixed</h2>
 * No WKWebView do iOS quem rola é o documento, não o body: `overflow: hidden` ali não impede o
 * arrasto, e o fundo continua deslizando atrás do sheet. O que segura é `position: fixed` no body —
 * mas isso joga a página para o topo, então a posição é salva antes e devolvida depois.
 *
 * <h2>Quem decide se a trava vale: o CSS</h2>
 * O JS não lê viewport. Ele adiciona a classe e escreve a posição em `--topo-travado`; a media query
 * de styles/index.css decide se aquilo significa alguma coisa — de md para cima o overlay é um
 * cartão centrado, e travar o body ali sumiria com a barra de rolagem e deslocaria a página em
 * ~15px.
 *
 * A devolução da rolagem pergunta ao `getComputedStyle` se a trava chegou a ser aplicada, em vez de
 * repetir o breakpoint aqui: no desktop a pessoa continua rolando com o diálogo aberto, e devolvê-la
 * à posição de quando ele abriu seria um pulo.
 */

// Do fundo para o topo. Cada entrada guarda a REF do fechamento, não a função: o `onFechar` de um
// componente ganha identidade nova a cada render do pai, e registrar a função obrigaria o efeito a
// depender dela — reinscrevendo o overlay na pilha a cada render.
const pilha = [];

let rolagemSalva = 0;

const CLASSE = 'travar-rolagem-mobile';

const travarRolagem = () => {
	rolagemSalva = window.scrollY;
	document.body.style.setProperty('--topo-travado', `-${rolagemSalva}px`);
	document.body.classList.add(CLASSE);
};

const destravarRolagem = () => {
	// Antes de tirar a classe, porque depois dela a resposta é sempre "static".
	const estavaTravado = window.getComputedStyle(document.body).position === 'fixed';

	document.body.classList.remove(CLASSE);
	document.body.style.removeProperty('--topo-travado');

	if (estavaTravado) window.scrollTo(0, rolagemSalva);
};

/**
 * Anuncia um overlay aberto e devolve a função que o remove — feita para ser o retorno de um
 * useEffect.
 *
 * @param {{ current: (() => void) | undefined }} aoFecharRef ref com o fechamento do overlay, lida
 *        só na hora em que o "voltar" pede para fechá-lo. Sem ela, o botão voltar chamaria a versão
 *        do `onFechar` que existia no render em que o overlay abriu.
 */
export function abrirOverlay(aoFecharRef) {
	const entrada = { aoFecharRef };
	pilha.push(entrada);
	if (pilha.length === 1) travarRolagem();

	return () => {
		const posicao = pilha.indexOf(entrada);
		if (posicao === -1) return;
		pilha.splice(posicao, 1);
		if (pilha.length === 0) destravarRolagem();
	};
}

/** Quantos overlays estão cobrindo a tela agora. */
export function temOverlayAberto() {
	return pilha.length > 0;
}

/**
 * Fecha o overlay do topo, se houver. Devolve true quando fechou alguma coisa — é o que permite ao
 * botão voltar do Android decidir entre "fechei o sheet" e "agora sim, navegue".
 *
 * Só o do topo: com dois empilhados, um "voltar" é um passo atrás, não uma demolição.
 */
export function fecharOverlayDoTopo() {
	const topo = pilha[pilha.length - 1];
	if (!topo) return false;

	const aoFechar = topo.aoFecharRef?.current;
	if (typeof aoFechar !== 'function') return false;

	aoFechar();
	return true;
}

/** Só para os testes: esvazia a pilha entre casos. */
export function _resetarOverlays() {
	pilha.length = 0;
	rolagemSalva = 0;
	document.body.classList.remove(CLASSE);
	document.body.style.removeProperty('--topo-travado');
}
